/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: ActiveCallsPage.tsx
 🗓️ Created: 2025-12-23
 🧩 Description:
    Active Calls page - displays active calls from FreeSWITCH.
    Uses the standard DataGridSimple layout consistent with other modules.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
 ────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useEffect } from 'react';
import { RefreshCw, PhoneCall, PhoneIncoming, PhoneOutgoing, Clock } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useNotifications } from '../../contexts/NotificationContext';
import { useContextStore } from '../../store/context';
import { Badge } from '../../components/ui/badge';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import { fetchActiveCalls, refreshActiveCalls, ActiveCall } from './api';

interface ActiveCallColumn {
    key: string;
    label: string;
    sortable?: boolean;
    visible: boolean;
    render?: (value: unknown, item: ActiveCall) => React.ReactNode;
}

export default function ActiveCallsPage() {
    const { t, i18n } = useI18n();
    const { showSuccess, showError } = useNotifications();
    const { selectedDomain } = useContextStore();

    // Effective domain ID for contextual filtering
    const effectiveDomainId = selectedDomain?.id;

    const [calls, setCalls] = useState<ActiveCall[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [sortBy, setSortBy] = useState('created_epoch');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

    // Format duration
    const formatDuration = useCallback((seconds: number) => {
        if (!seconds || seconds < 0) return '0s';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;

        if (hours > 0) {
            return `${hours}h ${minutes}m ${secs}s`;
        } else if (minutes > 0) {
            return `${minutes}m ${secs}s`;
        }
        return `${secs}s`;
    }, []);

    // Define columns
    const [columns, setColumns] = useState<ActiveCallColumn[]>([]);

    const availableColumns = useMemo<ActiveCallColumn[]>(() => [
        {
            key: 'direction',
            label: t('activeCalls.columns.direction'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: ActiveCall) => (
                <Badge
                    variant="outline"
                    className={`flex items-center gap-1 w-fit ${item.direction === 'inbound'
                        ? 'border-green-500 text-green-600'
                        : 'border-blue-500 text-blue-600'
                        }`}
                >
                    {item.direction === 'inbound' ? (
                        <PhoneIncoming className="h-3 w-3" />
                    ) : (
                        <PhoneOutgoing className="h-3 w-3" />
                    )}
                    {item.direction === 'inbound' ? t('activeCalls.inbound') : t('activeCalls.outbound')}
                </Badge>
            )
        },
        {
            key: 'cid_num',
            label: t('activeCalls.columns.caller'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: ActiveCall) => (
                <div className="flex flex-col">
                    <span className="font-mono font-semibold text-primary">{item.cid_num}</span>
                    <span className="text-xs text-muted-foreground">{item.cid_name}</span>
                </div>
            )
        },
        {
            key: 'dest',
            label: t('activeCalls.columns.destination'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: ActiveCall) => (
                <div className="flex flex-col">
                    <span className="font-mono font-semibold">{item.dest || item.callee_num}</span>
                    <span className="text-xs text-muted-foreground">{item.callee_name}</span>
                </div>
            )
        },
        {
            key: 'callstate',
            label: t('activeCalls.columns.state'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: ActiveCall) => {
                const stateColors: Record<string, string> = {
                    'ACTIVE': 'bg-green-500',
                    'RINGING': 'bg-yellow-500',
                    'EARLY': 'bg-orange-500',
                    'HELD': 'bg-purple-500',
                    'HANGUP': 'bg-red-500'
                };
                const colorClass = stateColors[item.callstate] || 'bg-gray-500';
                return (
                    <Badge className={`${colorClass} text-white`}>
                        {item.callstate}
                    </Badge>
                );
            }
        },
        {
            key: 'duration',
            label: t('activeCalls.columns.duration'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: ActiveCall) => (
                <span className="flex items-center gap-1 font-mono text-sm">
                    <Clock className="h-3 w-3 text-muted-foreground" />
                    {formatDuration(item.duration || 0)}
                </span>
            )
        },
        {
            key: 'ip_addr',
            label: t('activeCalls.columns.ipAddress'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: ActiveCall) => (
                <span className="font-mono text-sm">{item.ip_addr}</span>
            )
        },
        {
            key: 'read_codec',
            label: t('activeCalls.columns.codec'),
            sortable: false,
            visible: false,
            render: (_value: unknown, item: ActiveCall) => (
                <span className="text-sm">{item.read_codec}</span>
            )
        },
        {
            key: 'context',
            label: t('activeCalls.columns.context'),
            sortable: false,
            visible: false,
            render: (_value: unknown, item: ActiveCall) => (
                <span className="text-sm">{item.context}</span>
            )
        },
        {
            key: 'hostname',
            label: t('activeCalls.columns.hostname'),
            sortable: false,
            visible: false,
            render: (_value: unknown, item: ActiveCall) => (
                <span className="text-sm">{item.hostname}</span>
            )
        }
    ], [t, i18n.language, formatDuration]);

    // Initialize columns on mount
    useEffect(() => {
        setColumns(availableColumns);
    }, [availableColumns]);

    // Load data - showLoading controls whether to show loading spinner
    const loadData = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            const result = await fetchActiveCalls({ page, limit, search: searchQuery, domainId: effectiveDomainId });

            setCalls(result.data);
            setTotal(result.pagination?.total || 0);
            setTotalPages(result.pagination?.totalPages || 0);
        } catch (error) {
            showError(error instanceof Error ? error.message : t('activeCalls.messages.loadError'));
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [page, limit, searchQuery, effectiveDomainId, showError, t]);

    // Initial load with spinner
    useEffect(() => {
        loadData(true);
    }, [page, limit, searchQuery, effectiveDomainId]); // Only re-run on filter changes

    // Auto-refresh every 5 seconds (silent - no spinner)
    useEffect(() => {
        const interval = setInterval(() => {
            if (!loading && !refreshing) {
                loadData(false); // Silent refresh
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [loading, refreshing]);

    // Handlers
    const handleRefresh = useCallback(async () => {
        try {
            setRefreshing(true);
            await refreshActiveCalls();
            await loadData();
            showSuccess(t('activeCalls.messages.refreshSuccess'));
        } catch (error) {
            showError(error instanceof Error ? error.message : t('activeCalls.messages.refreshError'));
        } finally {
            setRefreshing(false);
        }
    }, [loadData, showSuccess, showError, t]);

    const handleSearch = useCallback((query: string) => {
        setSearchQuery(query);
        setPage(1);
    }, []);

    const handleSort = useCallback((column: string) => {
        if (sortBy === column) {
            setSortOrder(current => current === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    }, [sortBy]);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const handleItemsPerPageChange = useCallback((newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    }, []);

    const handleColumnToggle = useCallback((key: string) => {
        setColumns(current =>
            current.map(col =>
                col.key === key ? { ...col, visible: !col.visible } : col
            )
        );
    }, []);

    // Pagination object for DataGridSimple
    const pagination = useMemo(() => ({
        page,
        limit,
        total,
        totalPages
    }), [page, limit, total, totalPages]);

    return (
        <div className="p-6">
            {/* Data Grid */}
            <DataGridSimple
                data={calls}
                loading={loading}
                columns={columns}
                pagination={pagination}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={handleSearch}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onColumnToggle={handleColumnToggle}
                searchPlaceholder={t('activeCalls.searchPlaceholder')}
                title={t('activeCalls.title')}
                titleIcon={<PhoneCall className="h-5 w-5 text-primary" />}
                actions={
                    <div className="flex items-center gap-2">
                        <SimpleTooltip content={t('activeCalls.refresh')} position="top">
                            <button
                                onClick={handleRefresh}
                                disabled={refreshing}
                                className="p-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                            >
                                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                            </button>
                        </SimpleTooltip>
                    </div>
                }
            />
        </div>
    );
}
